import { useBreakpoint } from "../custom-hooks/useBreakpoint";
import { useCarousel } from "../custom-hooks/useCarousel";
import { Purpose } from "../domain/enums/Purpose";
import { IManagementFlat } from "../domain/models/Management";
import ImagePlaceholderSVG from "./shared/svg/ImagePlaceholder";
import { useMemo, useEffect } from "react";

interface LeadershipCardsBlockProps {
    leaders?: IManagementFlat[];
}

export default function LeadershipCardsBlock({ leaders }: LeadershipCardsBlockProps) {
    const { isMobile, isTablet, isDesktopSmall, isDesktopMedium, isDesktopLarge } = useBreakpoint();

    const leaderCount = leaders?.length ?? 0;

    // if there are <= 2 elements — show all of them in one slide,
    // otherwise form moving pairs [0,1],[1,2],...
    const tabletSlides = useMemo(() => {
        const res: number[][] = [];
        if (leaderCount === 0) return res;
        if (leaderCount <= 2) {
            res.push(Array.from({ length: leaderCount }, (_, i) => i));
            return res;
        }

        for (let i = 0; i < leaderCount - 1; i++) {
            res.push([i, i + 1]);
        }
        return res;
    }, [leaderCount]);

    const slidesCount = isMobile ? leaderCount : isTablet ? tabletSlides.length : 1;
    const { activeIndex, setActiveIndex } = useCarousel(slidesCount);

    // Reset the index if it is out of range after changing the number of slides
    useEffect(() => {
        if (slidesCount > 0 && activeIndex >= slidesCount) setActiveIndex(0);
    }, [slidesCount, activeIndex, setActiveIndex]);

    const cardsToShow = isMobile
        ? [activeIndex]
        : isTablet
        ? tabletSlides[activeIndex] ?? tabletSlides[0] ?? []
        : leaders
        ? leaders.map((_, i) => i)
        : [];

    const dotsCount = isMobile ? leaderCount : isTablet ? tabletSlides.length : 0;
    return (
        <div className="relative">
            <div className="flex flex-wrap justify-start gap-5 pt-[32px] md:pt-[42px] xl:pt-[50px]">
                {cardsToShow.map((idx) => (
                    <div
                        key={idx}
                        className={`flex flex-col items-start bg-white rounded-md p-4 gap-4
                            w-full
                            ${isTablet ? "md:w-[330px]" : ""}
                            ${isDesktopSmall ? "lap:w-[calc(50%-10px)]" : ""}
                            ${isDesktopMedium || isDesktopLarge ? "xl:w-[calc(33%-13px)]" : ""}`}
                    >
                        <div className="w-full h-[300px] md:h-[457px] flex items-center justify-center overflow-hidden"> 
                            {leaders?.[idx]?.pictureExists?.(Purpose.AVATAR) ? (
                                <img
                                    src={leaders?.[idx]?.getPictureUrl?.()}
                                    alt={`leadership-${idx}`}
                                    className="rounded-md object-cover w-full "
                                />
                            ) : (
                                <ImagePlaceholderSVG fill="#bec6d5" classname="justify-self-center" />
                            )}
                        </div>

                        <p className="w-full font-medium text-xl uppercase text-primary-400">
                            {leaders?.[idx]?.fullName}
                        </p>
                        <p className="w-full font-normal text-base text-text-300">{leaders?.[idx]?.position}</p>
                    </div>
                ))}
            </div>

            {(isMobile || isTablet) && (
                <div className="flex justify-center items-center gap-3 mt-4">
                    {Array.from({ length: dotsCount }).map((_, i) => (
                        <div
                            key={i}
                            onClick={() => setActiveIndex(i)}
                            className={`w-3 h-3 rounded-full cursor-pointer transition-colors duration-300 ${
                                i === activeIndex ? "bg-primary-600" : "bg-primary-500"
                            }`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
